import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, GraduationCap, MapPin, BookOpen, CheckCircle2, ShieldCheck, Globe2, Phone as PhoneIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { friendlyUploadError } from '../lib/errors';
import { compressImage } from '../lib/imageCompression';
import { uploadWithProgress } from '../lib/uploadWithProgress';

const API = process.env.REACT_APP_BACKEND_URL;

// Canonical lists — duplicated from the backend so the dropdowns NEVER render
// empty even if the optional `/api/public/student-signup/options` fetch fails
// (slow network, ad-blocker, stale CDN cache, etc.). The fetch below acts as
// a live override in case admin extends the list dynamically.
const DEFAULT_FIELDS_OF_STUDY = [
  'Agriculture & Environmental Studies',
  'Architecture & Construction',
  'Arts, Design & Media',
  'Business & Management',
  'Computer Science & IT',
  'Data Science & Analytics',
  'Education & Teaching',
  'Engineering',
  'Finance & Accounting',
  'Health Sciences & Nursing',
  'Hospitality & Tourism',
  'Law & Legal Studies',
  'Marketing & Communications',
  'Natural Sciences',
  'Social Sciences',
  'Trades & Skilled Crafts',
  'Other',
];
const DEFAULT_LOCATIONS = [
  'Lower Mainland Vancouver',
  'Vancouver Island',
  'Other parts of BC',
];

// Base documents required from EVERY student. International applicants must
// additionally upload a Study/Work Permit and an English-proficiency test
// document (handled at render time via `getRequiredDocs(student_status)`).
const BASE_DOCUMENTS = [
  { key: 'class_12_certificate', label: 'Class 12 Certificate' },
  { key: 'class_12_marksheet', label: 'Class 12 Marksheet' },
  { key: 'class_10_certificate', label: 'Class 10 Certificate' },
  { key: 'class_10_marksheet', label: 'Class 10 Marksheet' },
  { key: 'id_proof_primary', label: 'Student ID 1 (PR Card / Passport)' },
  { key: 'id_proof_secondary', label: 'Student ID 2 (Driving License or BCeID)' },
  { key: 'photo', label: 'Recent Photograph' },
];

// Returns required docs (validation gate) for the current student_status.
const getRequiredDocs = (studentStatus) =>
  studentStatus === 'international'
    ? [
        ...BASE_DOCUMENTS,
        { key: 'study_permit', label: 'Study Permit / Work Permit' },
        { key: 'english_proficiency_test', label: 'English Proficiency Proof (IELTS / TOEFL / PTE / Duolingo)' },
      ]
    : BASE_DOCUMENTS;

// Returns ALL slots to render (required + optional). The `optional` flag drives
// the "(optional)" label hint and skips validation.
const getRenderDocs = (studentStatus) => {
  const required = getRequiredDocs(studentStatus);
  const requiredKeys = new Set(required.map((d) => d.key));
  const optionalExtras = [
    { key: 'english_proficiency_test', label: 'English Proficiency Proof (IELTS / TOEFL / PTE / Duolingo)', optional: true },
    { key: 'diploma_transcript', label: 'Diploma / Degree Transcript', optional: true },
  ].filter((d) => !requiredKeys.has(d.key));
  return [...required, ...optionalExtras];
};

export default function StudentSignup() {
  const navigate = useNavigate();
  const [options, setOptions] = useState({
    fields_of_study: DEFAULT_FIELDS_OF_STUDY,
    locations: DEFAULT_LOCATIONS,
  });
  const [form, setForm] = useState({
    student_name: '',
    email: '',
    confirm_email: '',
    phone: '',
    dob: '',
    qualifications: { class_10_pct: '', class_12_pct: '', english_test: '', english_score: '' },
    field_of_study: '',
    preferred_location: '',
    marital_status: '',
    student_status: '',
    emergency_contact_name: '',
    emergency_contact_relation: '',
    emergency_contact_phone: '',
  });
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);   // null | 'compressing' | 0-100
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    // Live-override the defaults if the backend returns non-empty lists.
    // If the fetch fails (offline, ad-blocker, stale CDN), the defaults remain.
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/student-signup/options`);
        if (!res.ok) return;
        const data = await res.json();
        setOptions((prev) => ({
          fields_of_study: (data.fields_of_study && data.fields_of_study.length) ? data.fields_of_study : prev.fields_of_study,
          locations: (data.locations && data.locations.length) ? data.locations : prev.locations,
        }));
      } catch (_) { /* keep defaults */ }
    })();
  }, []);

  const setField = (k, v) => setForm({ ...form, [k]: v });
  const setQual = (k, v) => setForm({ ...form, qualifications: { ...form.qualifications, [k]: v } });
  const setFile = (k, f) => setFiles({ ...files, [k]: f });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Client-side validation of required pieces
    if (form.email.trim().toLowerCase() !== form.confirm_email.trim().toLowerCase()) {
      setError('Email and confirm email do not match. Please double-check for typos.');
      return;
    }
    if (!form.field_of_study) { setError('Please pick your field of study.'); return; }
    if (!form.preferred_location) { setError('Please pick where you want to study.'); return; }
    if (!form.marital_status) { setError('Please select your marital status.'); return; }
    if (!form.student_status) { setError('Please select Domestic or International.'); return; }
    const requiredDocs = getRequiredDocs(form.student_status);
    const missing = requiredDocs.filter((d) => !files[d.key]).map((d) => d.label);
    if (missing.length) { setError(`Please upload: ${missing.join(', ')}`); return; }

    setSubmitting(true);
    try {
      // STEP 1 — compress all image uploads on-device so the post body stays
      // under a few MB. Smartphone HEIC/JPEG photos drop from ~6 MB to ~300 KB
      // after this. PDFs / unknown types pass through unchanged.
      setProgress('compressing');
      const compressedEntries = await Promise.all(
        requiredDocs.map(async (d) => [d.key, await compressImage(files[d.key])])
      );
      const compressed = Object.fromEntries(compressedEntries);

      const data = new FormData();
      data.append('student_name', form.student_name);
      data.append('email', form.email);
      data.append('phone', form.phone);
      data.append('dob', form.dob);
      data.append('qualifications', JSON.stringify(form.qualifications));
      data.append('field_of_study', form.field_of_study);
      data.append('preferred_location', form.preferred_location);
      data.append('marital_status', form.marital_status);
      data.append('student_status', form.student_status);
      if (form.emergency_contact_name.trim()) data.append('emergency_contact_name', form.emergency_contact_name.trim());
      if (form.emergency_contact_relation.trim()) data.append('emergency_contact_relation', form.emergency_contact_relation.trim());
      if (form.emergency_contact_phone.trim()) data.append('emergency_contact_phone', form.emergency_contact_phone.trim());
      requiredDocs.forEach((d) => data.append(d.key, compressed[d.key]));
      // Also send any OPTIONAL docs the applicant chose to upload (English
      // Proficiency Proof for domestic, Diploma / Degree Transcript for all).
      const allSlots = getRenderDocs(form.student_status);
      for (const slot of allSlots) {
        if (slot.optional && files[slot.key]) {
          data.append(slot.key, await compressImage(files[slot.key]));
        }
      }

      // STEP 2 — XHR upload with progress UI so the user doesn't think
      // the app froze. Also: longer timeout than fetch's default ingress.
      const res = await uploadWithProgress(`${API}/api/public/student-signup`, {
        method: 'POST',
        body: data,
        onProgress: ({ pct }) => setProgress(pct ?? 'uploading'),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Application failed');
      setSuccess(body);
    } catch (err) {
      setError(friendlyUploadError(err, 'Application failed. Please try again.'));
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  if (success) {
    const emailSent = success.email_sent !== false; // backward-compat when older deploy doesn't return the flag
    return (
      <div className="min-h-screen bg-tge-paper flex items-center justify-center px-4 pt-[100px] pb-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-xl bg-white rounded-3xl shadow-[0_20px_60px_rgba(27,42,107,0.12)] p-8 sm:p-10 text-center"
          data-testid="student-signup-success"
        >
          <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center ${emailSent ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            <CheckCircle2 size={32} className={emailSent ? 'text-emerald-600' : 'text-amber-600'} />
          </div>
          <h1 className="font-serif-display text-3xl text-tge-navy mt-5">Application received</h1>
          <p className="text-sm text-tge-muted mt-3 leading-relaxed">
            Thanks, <strong>{form.student_name.split(' ')[0]}</strong>! Your student ID is{' '}
            <span className="font-mono text-tge-navy">{success.student_id}</span>.
          </p>

          {emailSent ? (
            <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-left">
              <p className="text-sm text-emerald-800 leading-relaxed">
                <strong>We've emailed {form.email}</strong> a secure link to set your password and access your portal.
              </p>
              <p className="text-xs text-emerald-800/80 mt-2 leading-relaxed">
                <strong>Didn't get it within 2 minutes?</strong>
                <br />1) Check spam / junk / promotions folders.
                <br />2) Whitelist <code>noreply@trueglobaleducation.com</code>.
                <br />3) Use "Forgot password" on the sign-in page — it sends a fresh link.
              </p>
            </div>
          ) : (
            <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-left">
              <p className="text-sm text-amber-900 font-semibold">
                Your application is saved, but we couldn't send the activation email right now.
              </p>
              <p className="text-xs text-amber-900/80 mt-2 leading-relaxed">
                Please contact <strong>info@trueglobaleducation.com</strong> and mention student ID{' '}
                <span className="font-mono">{success.student_id}</span>. We'll send the password-setup link manually.
              </p>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row items-center gap-3 justify-center">
            <Button onClick={() => navigate('/student-login')} className="bg-tge-navy hover:bg-tge-navy-deep text-white" data-testid="student-signup-go-login">
              Go to sign in
            </Button>
            <Link to="/forgot-password" className="text-sm text-tge-muted hover:text-tge-navy underline decoration-tge-gold/40 underline-offset-4">
              Resend setup link
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tge-paper">
      <main className="max-w-3xl mx-auto px-4 pt-[110px] pb-12">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs text-tge-muted hover:text-tge-navy transition mb-5" data-testid="student-signup-back">
          <ArrowLeft size={14} /> Back to all portals
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(27,42,107,0.10)] overflow-hidden"
          data-testid="student-signup-card"
        >
          <div className="bg-tge-navy text-white px-6 sm:px-10 py-8">
            <p className="uppercase tracking-[0.18em] text-[10px] font-semibold text-tge-gold mb-2">For Students</p>
            <h1 className="font-serif-display text-3xl sm:text-4xl">Apply directly</h1>
            <p className="text-sm text-tge-cream/80 mt-2 max-w-lg">
              Skip the agent. Fill in your details, upload your documents, and we'll route your application to the
              right college for you. Takes ~5 minutes.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="px-6 sm:px-10 py-8 space-y-7">
            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm" data-testid="student-signup-error">
                {error}
              </div>
            )}

            {/* About you */}
            <section>
              <h2 className="font-serif-display text-lg text-tge-navy mb-3 flex items-center gap-2">
                <GraduationCap size={18} className="text-tge-gold" /> About you
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Full name *</Label>
                  <Input required value={form.student_name} onChange={(e) => setField('student_name', e.target.value)} placeholder="Jane Doe" data-testid="student-signup-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input required type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} onPaste={(e) => e.preventDefault()} placeholder="you@example.com" data-testid="student-signup-email" />
                </div>
                <div>
                  <Label>Confirm email *</Label>
                  <Input
                    required
                    type="email"
                    value={form.confirm_email}
                    onChange={(e) => setField('confirm_email', e.target.value)}
                    onPaste={(e) => e.preventDefault()}
                    placeholder="Re-type your email — don't paste"
                    data-testid="student-signup-confirm-email"
                  />
                  {form.confirm_email && form.email && form.confirm_email.toLowerCase() !== form.email.toLowerCase() && (
                    <p className="text-[11px] text-rose-600 mt-1">Doesn't match the email above.</p>
                  )}
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input required value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="+91 98765 43210" data-testid="student-signup-phone" />
                </div>
                <div>
                  <Label>Date of birth *</Label>
                  <Input required type="date" value={form.dob} onChange={(e) => setField('dob', e.target.value)} data-testid="student-signup-dob" />
                </div>
              </div>
            </section>

            {/* Qualifications */}
            <section>
              <h2 className="font-serif-display text-lg text-tge-navy mb-3 flex items-center gap-2">
                <BookOpen size={18} className="text-tge-gold" /> Academic background
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Class 10 percentage / GPA</Label>
                  <Input value={form.qualifications.class_10_pct} onChange={(e) => setQual('class_10_pct', e.target.value)} placeholder="e.g. 85% or 3.6 GPA" data-testid="student-signup-class10" />
                </div>
                <div>
                  <Label>Class 12 percentage / GPA</Label>
                  <Input value={form.qualifications.class_12_pct} onChange={(e) => setQual('class_12_pct', e.target.value)} placeholder="e.g. 78%" data-testid="student-signup-class12" />
                </div>
                <div>
                  <Label>English test (if any)</Label>
                  <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.qualifications.english_test} onChange={(e) => setQual('english_test', e.target.value)} data-testid="student-signup-eng-test">
                    <option value="">None / not yet</option>
                    <option value="IELTS">IELTS</option>
                    <option value="TOEFL">TOEFL</option>
                    <option value="PTE">PTE</option>
                    <option value="Duolingo">Duolingo English Test</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>English test score</Label>
                  <Input value={form.qualifications.english_score} onChange={(e) => setQual('english_score', e.target.value)} placeholder="e.g. IELTS 6.5" data-testid="student-signup-eng-score" />
                </div>
              </div>
            </section>

            {/* Preferences */}
            <section>
              <h2 className="font-serif-display text-lg text-tge-navy mb-3 flex items-center gap-2">
                <MapPin size={18} className="text-tge-gold" /> Where & what you want to study
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Field of study *</Label>
                  <select
                    required
                    value={form.field_of_study}
                    onChange={(e) => setField('field_of_study', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="student-signup-field-of-study"
                  >
                    <option value="">Select a field…</option>
                    {options.fields_of_study.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Where do you want to study? *</Label>
                  <select
                    required
                    value={form.preferred_location}
                    onChange={(e) => setField('preferred_location', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="student-signup-location"
                  >
                    <option value="">Select a location…</option>
                    {options.locations.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* Marital status + Domestic/International */}
            <section>
              <h2 className="font-serif-display text-lg text-tge-navy mb-3 flex items-center gap-2">
                <Globe2 size={18} className="text-tge-gold" /> Marital status & residency
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Marital status *</Label>
                  <select
                    required
                    value={form.marital_status}
                    onChange={(e) => setField('marital_status', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="student-signup-marital-status"
                  >
                    <option value="">Select…</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                    <option value="Separated">Separated</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Student status *</Label>
                  <select
                    required
                    value={form.student_status}
                    onChange={(e) => setField('student_status', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="student-signup-student-status"
                  >
                    <option value="">Select…</option>
                    <option value="domestic">Domestic</option>
                    <option value="international">International</option>
                  </select>
                </div>
              </div>
              {form.student_status === 'international' && (
                <p className="mt-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="international-docs-notice">
                  As an international student you must also upload a <strong>Study Permit / Work Permit</strong>
                  {' '}and an <strong>English Proficiency Test</strong> document below.
                </p>
              )}
            </section>

            {/* Emergency contact — optional but encouraged */}
            <section>
              <h2 className="font-serif-display text-lg text-tge-navy mb-3 flex items-center gap-2">
                <PhoneIcon size={18} className="text-tge-gold" /> Emergency contact
                <span className="text-[11px] font-normal text-tge-muted">(optional)</span>
              </h2>
              <p className="text-xs text-tge-muted mb-3">Someone we should contact in case of an emergency — not the student themselves.</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label>Full name</Label>
                  <Input
                    value={form.emergency_contact_name}
                    onChange={(e) => setField('emergency_contact_name', e.target.value)}
                    placeholder="e.g. Robert Doe"
                    data-testid="student-signup-emergency-name"
                  />
                </div>
                <div>
                  <Label>Relation</Label>
                  <select
                    value={form.emergency_contact_relation}
                    onChange={(e) => setField('emergency_contact_relation', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="student-signup-emergency-relation"
                  >
                    <option value="">Select…</option>
                    <option value="Parent">Parent</option>
                    <option value="Father">Father</option>
                    <option value="Mother">Mother</option>
                    <option value="Guardian">Guardian</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Friend">Friend</option>
                    <option value="Relative">Relative</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Contact number</Label>
                  <Input
                    type="tel"
                    value={form.emergency_contact_phone}
                    onChange={(e) => setField('emergency_contact_phone', e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    data-testid="student-signup-emergency-phone"
                  />
                </div>
              </div>
            </section>

            {/* Documents */}
            <section>
              <h2 className="font-serif-display text-lg text-tge-navy mb-3 flex items-center gap-2">
                <Upload size={18} className="text-tge-gold" /> Documents
              </h2>
              <p className="text-xs text-tge-muted mb-3">Accepted: PDF, JPG, PNG · up to 10 MB each.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {getRenderDocs(form.student_status).map((d) => (
                  <label
                    key={d.key}
                    className={`flex flex-col gap-1 p-3 border rounded-xl cursor-pointer transition ${files[d.key] ? 'border-emerald-300 bg-emerald-50/40' : 'border-tge-cream hover:border-tge-gold/40'}`}
                    data-testid={`student-signup-doc-${d.key}`}
                  >
                    <span className="text-[12px] font-semibold text-tge-navy">{d.label}{d.optional ? ' (optional)' : ' *'}</span>
                    <input
                      type="file"
                      accept="application/pdf,image/png,image/jpeg"
                      required={!d.optional}
                      onChange={(e) => setFile(d.key, e.target.files?.[0] || null)}
                      className="text-xs"
                    />
                    {files[d.key] && (
                      <span className="text-[10px] text-emerald-700 truncate">✓ {files[d.key].name}</span>
                    )}
                  </label>
                ))}
              </div>
            </section>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-tge-navy hover:bg-tge-navy-deep text-white font-semibold disabled:opacity-60"
              data-testid="student-signup-submit"
            >
              {submitting ? 'Submitting…' : (<><ShieldCheck size={16} className="mr-2" /> Submit application</>)}
            </Button>
            {submitting && (
              <div className="mt-2" data-testid="student-signup-progress">
                <div className="flex justify-between text-[11px] text-tge-muted mb-1">
                  <span>
                    {progress === 'compressing' && 'Optimising your photos…'}
                    {progress === 'uploading' && 'Uploading documents…'}
                    {typeof progress === 'number' && `Uploading documents… ${progress}%`}
                    {progress == null && 'Preparing…'}
                  </span>
                  <span>{typeof progress === 'number' ? `${progress}%` : ''}</span>
                </div>
                <div className="w-full h-1.5 bg-tge-cream rounded-full overflow-hidden">
                  <div
                    className="h-full bg-tge-gold transition-all duration-300"
                    style={{ width: typeof progress === 'number' ? `${progress}%` : '10%' }}
                  />
                </div>
                <p className="text-[10px] text-tge-muted mt-1">
                  Please don't close this tab — your application is being saved.
                </p>
              </div>
            )}

            <p className="text-center text-xs text-tge-muted">
              Already applied?{' '}
              <Link to="/student-login" className="text-tge-navy font-semibold underline decoration-tge-gold/40 underline-offset-4" data-testid="student-signup-signin-link">
                Sign in to your portal
              </Link>
            </p>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
